import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../utils/api';
import { MiniPill, SectionLabel } from '../components/ui';

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
      {message}
    </div>
  );
}

const SECTIONS = [
  { label: 'Onboarding', types: ['invite', 'welcome'] },
  { label: 'Payroll', types: ['window_open', 'reminder', 'final_reminder', 'submission', 'confirmation', 'escalation'] },
  { label: 'Account', types: ['password_reset'] },
];

const USAGE = {
  invite: 'Sent when you invite a new client',
  welcome: 'Sent after a client accepts their invite',
  window_open: 'Payroll window open — in the send schedule',
  reminder: 'Payroll reminder — in the send schedule',
  final_reminder: 'Final reminder — in the send schedule',
  submission: 'Sent when a client submits changes',
  confirmation: 'Sent when payroll is processed',
  escalation: 'Overdue escalation — in the send schedule',
  password_reset: 'Sent on password reset',
};

// Sample values so the live preview reads naturally; unknown keys fall back to
// a bracketed placeholder.
const SAMPLE_VALUES = {
  client_name: 'Sarah', contact_name: 'Sarah', first_name: 'Sarah', name: 'Sarah',
  company_name: 'Sourcechange Limited',
  pay_day: 'Friday 29 May', pay_date: 'Friday 29 May',
  deadline_date: 'Monday 26 May', deadline: 'Monday 26 May',
  days_remaining: '3', days_left: '3',
  period_name: 'May 2026', month: 'May', month_name: 'May', year: '2026',
  submit_url: '#', invite_url: '#', inviteUrl: '#', reset_url: '#', url: '#', link: '#',
  admin_name: 'Fred', sender_name: 'Fred',
};
const sampleFor = (key) => (SAMPLE_VALUES[key] !== undefined ? SAMPLE_VALUES[key] : `[${key.replace(/_/g, ' ')}]`);

const TOKEN_RE = /(\*\*[^*]+\*\*|\{\{[^}]+\}\})/g;

// Render a single line with **bold** and {{variable}} substitution into nodes.
function renderInline(text, keyPrefix) {
  if (!text) return null;
  return text.split(TOKEN_RE).map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('{{') && part.endsWith('}}')) {
      return <span key={`${keyPrefix}-${i}`}>{sampleFor(part.slice(2, -2).trim())}</span>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

function PreviewBody({ body }) {
  const paragraphs = (body || '').split(/\n\s*\n/);
  return paragraphs.map((p, i) => (
    <p key={i} className="leading-relaxed mb-3">
      {p.split('\n').map((line, j) => (
        <React.Fragment key={j}>
          {j > 0 && <br />}
          {renderInline(line, `${i}-${j}`)}
        </React.Fragment>
      ))}
    </p>
  ));
}

const EMPTY_FORM = { subject: '', heading: '', body: '', button_text: '', footer_text: '' };

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');

  const fieldRefs = { subject: useRef(null), heading: useRef(null), body: useRef(null), button_text: useRef(null), footer_text: useRef(null) };
  const activeFieldRef = useRef('body');

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/admin/email-templates');
      setTemplates(res.data);
      setSelectedType(prev => prev || res.data[0]?.type || null);
    } catch (err) {
      setToast({ message: 'Failed to load email templates', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const currentTemplate = useMemo(() => templates.find(t => t.type === selectedType), [templates, selectedType]);

  // Load the selected template into the form whenever the selection changes.
  useEffect(() => {
    if (currentTemplate) {
      setForm({
        subject: currentTemplate.subject || '',
        heading: currentTemplate.heading || '',
        body: currentTemplate.body || '',
        button_text: currentTemplate.button_text || '',
        footer_text: currentTemplate.footer_text || '',
      });
      setConfirmReset(false);
    }
  }, [currentTemplate]);

  const hasChanges = currentTemplate && (
    form.subject !== (currentTemplate.subject || '') ||
    form.heading !== (currentTemplate.heading || '') ||
    form.body !== (currentTemplate.body || '') ||
    form.button_text !== (currentTemplate.button_text || '') ||
    form.footer_text !== (currentTemplate.footer_text || '')
  );

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    const handler = (e) => { if (hasChanges) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  const selectTemplate = (type) => {
    if (type === selectedType) return;
    if (hasChanges && !window.confirm('Discard unsaved changes to this template?')) return;
    setSelectedType(type);
  };

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const insertVariable = (key) => {
    const field = activeFieldRef.current || 'body';
    const el = fieldRefs[field].current;
    const token = `{{${key}}}`;
    const value = form[field] || '';
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setForm(prev => ({ ...prev, [field]: next }));
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + token.length; el.setSelectionRange(pos, pos); }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${selectedType}`, form);
      setToast({ message: 'Template saved successfully', type: 'success' });
      await fetchTemplates();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to save template', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirmReset) { setConfirmReset(true); return; }
    setSaving(true);
    try {
      const res = await api.post(`/admin/email-templates/${selectedType}/reset`);
      setForm({
        subject: res.data.subject || '', heading: res.data.heading || '', body: res.data.body || '',
        button_text: res.data.button_text || '', footer_text: res.data.footer_text || '',
      });
      setToast({ message: 'Template reset to default', type: 'success' });
      setConfirmReset(false);
      await fetchTemplates();
    } catch (err) {
      setToast({ message: 'Failed to reset template', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const inputCls = 'w-full rounded-md border-gray-300 text-[13.5px] focus:border-brand-500 focus:ring-brand-500';
  const labelCls = 'block text-[12px] uppercase tracking-wider font-bold text-gray-500 mb-1.5';
  const focusProps = (field) => ({
    ref: fieldRefs[field],
    onFocus: () => { activeFieldRef.current = field; },
  });

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Email Templates</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[640px]">
          Edit the subject lines and body of automated emails. Use <code className="text-[12px] bg-gray-100 px-1 py-0.5 rounded font-mono">{'{{variables}}'}</code> for dynamic values and <code className="text-[12px] bg-gray-100 px-1 py-0.5 rounded font-mono">**bold**</code> for emphasis.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[440px_minmax(0,1fr)] items-start">
        {/* Left rail — template list */}
        <aside className="space-y-5">
          {SECTIONS.map(section => {
            const items = section.types.map(type => templates.find(t => t.type === type)).filter(Boolean);
            if (items.length === 0) return null;
            return (
              <div key={section.label}>
                <SectionLabel count={items.length}>{section.label}</SectionLabel>
                <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden">
                  {items.map(t => {
                    const active = t.type === selectedType;
                    return (
                      <button key={t.type} onClick={() => selectTemplate(t.type)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-0 transition-colors ${active ? 'bg-brand-50/80' : 'hover:bg-gray-50'}`}
                        style={{ borderLeft: active ? '3px solid #ab63f2' : '3px solid transparent', paddingLeft: 13 }}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className={`text-[13.5px] font-semibold ${active ? 'text-brand-900' : 'text-ink'}`}>{t.name}</span>
                          {t.is_default ? <MiniPill tone="gray">Default</MiniPill> : <MiniPill tone="brand">Customised</MiniPill>}
                        </div>
                        <p className="text-[12px] text-gray-500 truncate">{t.subject}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </aside>

        {/* Main column — editor (top) + preview (below) */}
        <main className="space-y-4 min-w-0">
          {!currentTemplate ? (
            <div className="bg-white rounded-xl ring-1 ring-gray-200 px-6 py-16 text-center text-gray-500">Select a template to edit.</div>
          ) : (
            <>
              {/* Editor card */}
              <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3 bg-gray-50/40">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-brand-700">Editing</p>
                    <h4 className="text-[18px] text-ink mt-0.5">{currentTemplate.name}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    {!currentTemplate.is_default && (
                      <button onClick={handleReset}
                        className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium ${confirmReset ? 'text-white bg-rose-600 hover:bg-rose-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                        {confirmReset ? 'Confirm reset' : 'Reset to default'}
                      </button>
                    )}
                    {confirmReset && <button onClick={() => setConfirmReset(false)} className="text-[12.5px] text-gray-400 hover:text-gray-600">Cancel</button>}
                    <button onClick={handleSave} disabled={saving || !hasChanges}
                      className="px-4 py-1.5 rounded-md text-[13px] font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>

                <div className="px-5 py-5">
                  {/* Variables */}
                  {(currentTemplate.variables || []).length > 0 && (
                    <div className="mb-5">
                      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Available variables · click to insert</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(currentTemplate.variables || []).map(v => (
                          <code key={v.key} title={v.label} onMouseDown={(e) => e.preventDefault()} onClick={() => insertVariable(v.key)}
                            className="px-2 py-1 rounded bg-gray-100 hover:bg-brand-100 hover:text-brand-700 cursor-pointer text-[11.5px] font-mono text-gray-700">
                            {`{{${v.key}}}`}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid gap-4 mb-4 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Subject</label>
                      <input type="text" className={inputCls} value={form.subject} {...focusProps('subject')} onChange={(e) => handleChange('subject', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Heading</label>
                      <input type="text" className={inputCls} value={form.heading} {...focusProps('heading')} onChange={(e) => handleChange('heading', e.target.value)} />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className={labelCls}>Body <span className="normal-case font-normal text-gray-400 ml-1">separate paragraphs with blank lines · **bold**</span></label>
                    <textarea rows={7} className={`${inputCls} font-mono text-[13px]`} value={form.body} {...focusProps('body')} onChange={(e) => handleChange('body', e.target.value)} />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Button text</label>
                      <input type="text" className={inputCls} value={form.button_text} {...focusProps('button_text')} onChange={(e) => handleChange('button_text', e.target.value)} placeholder="(no button)" />
                    </div>
                    <div>
                      <label className={labelCls}>Footer</label>
                      <input type="text" className={inputCls} value={form.footer_text} {...focusProps('footer_text')} onChange={(e) => handleChange('footer_text', e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="px-5 py-3 bg-gray-50/40 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-[12px] text-gray-500">
                  <span>{currentTemplate.updated_by_name ? `Last edited by ${currentTemplate.updated_by_name}, ${new Date(currentTemplate.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Using the default template'}</span>
                  <span>Used by: <strong className="text-gray-700">{USAGE[currentTemplate.type] || currentTemplate.name}</strong></span>
                </div>
              </div>

              {/* Live preview card */}
              <div className="bg-gradient-to-b from-gray-100 to-gray-50 rounded-xl ring-1 ring-gray-200 px-6 py-6">
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Live preview</p>
                  <div className="flex items-center gap-1 bg-white ring-1 ring-gray-200 rounded-md p-0.5">
                    <button onClick={() => setPreviewMode('desktop')} className={`px-2.5 py-1 rounded text-[11.5px] font-semibold ${previewMode === 'desktop' ? 'bg-brand-100 text-brand-700' : 'text-gray-500'}`}>Desktop</button>
                    <button onClick={() => setPreviewMode('mobile')} className={`px-2.5 py-1 rounded text-[11.5px] font-semibold ${previewMode === 'mobile' ? 'bg-brand-100 text-brand-700' : 'text-gray-500'}`}>Mobile</button>
                  </div>
                </div>
                <div className="mx-auto rounded-lg ring-1 ring-gray-200 shadow-sm overflow-hidden transition-all bg-white"
                  style={{ maxWidth: previewMode === 'mobile' ? 360 : 720 }}>
                  {/* Gmail-style chrome */}
                  <div className="px-4 py-2.5 bg-gray-100 border-b border-gray-200 flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full bg-rose-400" />
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="ml-2 text-gray-500 truncate">noreply@startupaccountancy.co.uk → sarah@sourcechange.co.uk</span>
                  </div>

                  {/* Logo header — matches the real sent emails */}
                  <div className="px-8 pt-8 pb-4 text-center">
                    <img src="/Logo in black.png" alt="Startup Accountancy" className="h-12 mx-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </div>

                  {/* Brand-purple divider */}
                  <div className="mx-8" style={{ height: 1, background: '#5e2189' }} />

                  {/* Body content */}
                  <div className="px-8 py-6 text-[14px] text-gray-700">
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">Subject</p>
                    <p className="text-[13.5px] text-gray-800 mb-5">{renderInline(form.subject, 'subj')}</p>

                    {form.heading && (
                      <h3 className="font-heading text-[20px] text-ink mb-3">{renderInline(form.heading, 'head')}</h3>
                    )}

                    <PreviewBody body={form.body} />

                    {form.button_text && (
                      <div className="mt-2">
                        <span className="inline-block px-6 py-3 bg-brand-600 text-white rounded-md text-[14px] font-semibold">
                          {renderInline(form.button_text, 'btn')}
                        </span>
                      </div>
                    )}

                    {form.footer_text && (
                      <div className="border-t border-gray-100 mt-6 pt-3">
                        <p className="text-[12px] text-gray-500">{renderInline(form.footer_text, 'foot')}</p>
                      </div>
                    )}
                  </div>

                  {/* Bottom branding footer */}
                  <div className="px-8 pb-6 text-center">
                    <p className="text-[11px] text-gray-500">Startup Accountancy · Payroll Portal</p>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Need help? Email <a href="mailto:payroll@startupaccountancy.com" className="text-brand-700">payroll@startupaccountancy.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
